import { apiClient } from "@/lib/api";
import {
  StandardResponse,
  PaginatedStandardResponse,
  StandardResponseWithMessage,
  MessageResponse,
  ErrorResponse,
  PaginationInfo,
  isErrorResponse,
} from "@/types/api-response";

/**
 * Payment statuses, mirroring the backend PaymentStatus enum member for member. There is no
 * "Rejected": rejecting a payment moves it to Failed.
 */
export const PAYMENT_STATUSES = [
  "Pending",
  "AwaitingVerification",
  "Completed",
  "Failed",
  "Cancelled",
  "Refunded",
] as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/**
 * Payment methods, mirroring the backend PaymentMethod enum. Trial and Balance are included:
 * both settle as real payment rows that show up in this list, so an operator must be able to
 * filter for them. They differ in what they settled with — Balance moved the customer's own money
 * and is refundable, a Trial moved none and is not.
 */
export const PAYMENT_METHODS = [
  "QRCode",
  "BankTransfer",
  "Trial",
  "Balance",
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * DTO for payment list item in admin payment management
 */
export interface PaymentListDto {
  paymentId: string;
  paymentOrderNumber: string;
  tenantId: string;
  tenantName: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone?: string;
  amount: number; // in dollars
  currency: string;
  paymentMethod: PaymentMethod;
  /** QR gateway that issued this bill, null when it was never gateway-issued. */
  qrProvider?: string | null;
  /** Bill code T-PayGate issued for this payment's QR; null for any other payment. */
  tPayGateBillCode?: string | null;
  status: PaymentStatus;
  createdAt: string;
  verifiedAt?: string;
  completedAt?: string;
  verifiedBy?: string;
  /**
   * Who verified this payment, resolved server-side from `verifiedBy`. Absent when the payment
   * was never verified or that account no longer exists — never render the raw `verifiedBy` id
   * in its place.
   */
  verifiedByEmail?: string;
  /**
   * Who created this payment. Usually the tenant's own user; an operator when it was raised from
   * the console. Absent when that account no longer exists.
   */
  createdByEmail?: string;
  orderId?: string;
  invoiceId?: string;
  /** Human-readable order number (WIC-ORD-…), absent when the payment settles no order. */
  orderNumber?: string;
  /** NewService | Renewal | Upgrade | Addon — the order's type, not its status. */
  orderType?: string;
  /**
   * The payment's note, absent when it carries none. One field with two jobs: an operator's own
   * note (the Note action) and whatever the last decision left behind — verification notes, a
   * rejection reason, "Refunded by admin. Reason: …". The customer reads the same text.
   */
  verificationNotes?: string;
  proofs?: PaymentProofDto[];
}

/**
 * One proof file of a payment, as returned by the payments list.
 *
 * `downloadUrl` is API-relative and carries no `/api/v1` prefix — prepend it, the same way
 * every other attachment link in this console does.
 */
export interface PaymentProofDto {
  attachmentId?: string;
  downloadUrl: string;
  fileName: string;
  contentType?: string;
}

/**
 * DTO for attachment reference in payment context
 */
export interface PaymentAttachmentDto {
  refId: string;
  attachmentId: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  downloadUrl: string;
  purpose?: string;
  displayOrder: number;
  createdAt: string;
}

/**
 * DTO for payment verification queue item
 */
export interface PaymentVerificationDto {
  paymentId: string;
  paymentOrderNumber: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  /** QR gateway that issued this bill, null when it was never gateway-issued. */
  qrProvider?: string | null;
  paymentProofUrl?: string;
  orderId?: string;
  invoiceId?: string;
  createdAt: string;
  status: PaymentStatus;
  attachments: PaymentAttachmentDto[];
}

/**
 * DTO for detailed payment information
 */
export interface PaymentDetailDto {
  paymentId: string;
  paymentOrderNumber: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string;
  amount: number; // in dollars
  currency: string;
  paymentMethod: PaymentMethod;
  /** QR gateway that issued this bill, null when it was never gateway-issued. */
  qrProvider?: string | null;
  /** Bill code T-PayGate issued for this payment's QR; null for any other payment. */
  tPayGateBillCode?: string | null;
  status: PaymentStatus;
  paymentProofUrl?: string;
  tPaymentTransactionId?: string;
  tPaymentRedirectUrl?: string;
  /**
   * The payment's note. Written by a decision (verification notes, rejection reason, refund
   * sentence) and editable by a SuperAdmin in any status through `updatePaymentNote`. The customer
   * reads the same text on their payment screen.
   */
  verificationNotes?: string;
  verifiedBy?: string;
  verifiedByName?: string;
  /**
   * Who decided this payment. Absent when it was never decided or that account no longer exists —
   * render nothing in that case, never the raw `verifiedBy` id.
   */
  verifiedByEmail?: string;
  verifiedAt?: string;
  completedAt?: string;
  createdAt: string;
  order?: OrderInfoDto;
  invoice?: InvoiceInfoDto;
  attachments: PaymentAttachmentDto[];
}

/**
 * DTO for order information
 */
export interface OrderInfoDto {
  orderId: string;
  orderNumber: string;
  status: string;
  /**
   * Backend `OrderType` member name — "NewService", "Renewal", "BalanceTopUp", … Every payment
   * carries an order now, so the ABSENCE of one no longer marks a wallet top-up; this is what
   * tells the two apart.
   */
  type: string;
  total: number; // in dollars
}

/**
 * DTO for invoice information
 */
export interface InvoiceInfoDto {
  invoiceId: string;
  invoiceNumber: string;
  status: string;
  total: number; // in dollars
}

/**
 * What refunding a payment would and would not do, read before the operator commits.
 * Mirrors `RefundPreviewDto`.
 */
export interface RefundPreviewDto {
  paymentId: string;
  /** False for a top-up or any payment the handler would refuse; `refusalReason` says why. */
  canRefund: boolean;
  refusalReason?: string | null;
  /**
   * The amount that would actually be credited, in the same major-unit scale as `payment.amount`;
   * zero when the refund is refused. Equal to the payment's amount on a transfer or QR payment. On
   * a Balance settlement it is the cash the ledger says the order took, which can be LESS than the
   * payment's amount when sign-up bonus credit funded part of it.
   */
  refundAmount: number;
  /** False when this amount is already in the balance from an earlier credit. */
  wouldCreditWallet: boolean;
  order?: RefundPreviewOrderDto | null;
}

/** The order a refund leaves untouched, and what was built for it. */
export interface RefundPreviewOrderDto {
  orderId: string;
  orderNumber: string;
  status: string;
  /** Backend `OrderType` member name — see `OrderInfoDto.type`. */
  type: string;
  resourceCount: number;
  /** Machines that really exist. A refund does not delete these — an operator does, by hand. */
  provisionedResources: RefundPreviewResourceDto[];
}

/** One machine left behind by a refund, named so an operator can find it. */
export interface RefundPreviewResourceDto {
  resourceId: string;
  name: string;
  status: string;
  vmUuid: string;
}

/**
 * DTO for payment verification result
 */
export interface PaymentVerifiedDto {
  paymentId: string;
  tenantId: string;
  amount: number; // major currency unit (same scale as input)
  newBalance: number; // major currency unit
  verifiedAt: string;
  verifiedBy: string;
  /** Ledger transaction for the balance credit; null when the verification moved no money
   *  (an order-linked payment is settled by OrderPaidEvent and credits nothing). */
  transactionId: string | null;
}

/**
 * DTO for payment rejection result
 */
export interface PaymentRejectedDto {
  paymentId: string;
  tenantId: string;
  reason: string;
  rejectedAt: string;
  rejectedBy: string;
}

/**
 * DTO for payment refund result
 */
export interface PaymentRefundedDto {
  paymentId: string;
  tenantId: string;
  amount: number; // major currency unit
  newBalance: number; // major currency unit
  refundedAt: string;
  refundedBy: string;
  transactionId: string;
}

export interface PaymentVerificationRequest {
  notes?: string;
  /**
   * The amount the operator confirms actually arrived, on the same scale the console displays
   * and the list/detail DTOs carry — major units, sent back exactly as received.
   * Required by the server: the wallet is credited with the figure the CUSTOMER requested, so a
   * human has to affirm it against the receipt first. A value that does not match is refused
   * (AMOUNT_MISMATCH) rather than credited.
   */
  receivedAmount: number;
}

export interface PaymentRejectionRequest {
  reason: string;
}

export interface PaymentRefundRequest {
  reason?: string;
}

/**
 * Payment list filters
 */
export interface PaymentListFilters {
  page?: number;
  pageSize?: number;
  status?: PaymentStatus;
  paymentMethod?: PaymentMethod;
  tenantId?: string;
  /**
   * Created-at range, already formatted for the API: the picked calendar day carrying this client's
   * UTC offset (`2026-08-24T00:00:00+07:00`). The server expands each end to the whole of that day
   * in the caller's frame — see GetPaymentsQueryHandler.
   */
  fromDate?: string;
  toDate?: string;
  /** Matched against payment order number, owner e-mail and owner phone number. */
  search?: string;
}

/**
 * Admin payment service for handling payment verification operations.
 *
 * All endpoints return StandardResponse<T> or PaginatedStandardResponse<T> formats
 * with proper error handling and type safety.
 */
/** Request body for the bulk cancellation of unsettled payments. */
export interface BulkCancelPaymentsRequest {
  paymentIds: string[];
  /** Reaches the customer: stored as the payment's note and repeated in their audit trail. */
  reason: string;
}

/** What happened to one payment inside a bulk cancellation. */
export interface BulkCancelPaymentResult {
  paymentId: string;
  /** Empty when the row could not be loaded at all. */
  paymentOrderNumber: string;
  success: boolean;
  /** PAYMENT_NOT_FOUND | PAYMENT_NOT_CANCELLABLE | PAYMENT_CONFLICT | CANCEL_FAILED. */
  errorCode?: string | null;
  errorMessage?: string | null;
}

/** The per-payment report a bulk cancellation returns. */
export interface BulkCancelPaymentsResult {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: BulkCancelPaymentResult[];
}

export const adminPaymentService = {
  /**
   * Gets all payments with filters (paginated).
   * Returns all payments with optional filters for status, payment method, date range, tenant, etc.
   *
   * @param filters - Filter parameters including pagination
   * @returns Paginated list of payments
   * @throws Error if response is an error response
   */
  async getPayments(
    filters: PaymentListFilters = {},
    signal?: AbortSignal,
  ): Promise<{ data: PaymentListDto[]; pagination: PaginationInfo }> {
    const response = await apiClient.get<
      PaginatedStandardResponse<PaymentListDto> | ErrorResponse
    >("/api/v1/admin/payments", {
      signal,
      params: {
        page: filters.page ?? 1,
        limit: filters.pageSize ?? 50,
        ...(filters.status && { status: filters.status }),
        ...(filters.paymentMethod && { paymentMethod: filters.paymentMethod }),
        ...(filters.tenantId && { tenantId: filters.tenantId }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate }),
        ...(filters.search && { search: filters.search }),
      },
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },
  /**
   * Gets the payment verification queue (paginated).
   * Returns payments awaiting verification grouped by payment method.
   *
   * @param page - Page number (1-based, default 1)
   * @param limit - Items per page (default 20, max 100)
   * @param paymentMethod - Filter by payment method (optional)
   * @returns Paginated list of payments awaiting verification
   * @throws Error if response is an error response
   */
  async getVerificationQueue(
    page: number = 1,
    limit: number = 50,
    paymentMethod?: PaymentMethod,
  ): Promise<{ data: PaymentVerificationDto[]; pagination: PaginationInfo }> {
    const response = await apiClient.get<
      PaginatedStandardResponse<PaymentVerificationDto> | ErrorResponse
    >("/api/v1/admin/payments/verification-queue", {
      params: {
        page,
        limit,
        ...(paymentMethod && { paymentMethod }),
      },
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  /**
   * Gets payment details with proof and related order/invoice information.
   *
   * @param paymentId - The payment ID
   * @returns Payment detail information
   * @throws Error if payment not found or response is an error response
   */
  async getPaymentDetails(paymentId: string): Promise<PaymentDetailDto> {
    const response = await apiClient.get<
      StandardResponse<PaymentDetailDto> | ErrorResponse
    >(`/api/v1/admin/payments/${paymentId}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Verifies a payment (admin approval).
   * Marks payment as verified and creates account balance transaction.
   *
   * @param paymentId - The payment ID
   * @param request - Verification request with optional notes
   * @returns Payment verification result with new balance and transaction ID
   * @throws Error if payment not found, invalid status, or response is an error response
   */
  async verifyPayment(
    paymentId: string,
    request: PaymentVerificationRequest,
  ): Promise<PaymentVerifiedDto> {
    const response = await apiClient.post<
      StandardResponseWithMessage<PaymentVerifiedDto> | ErrorResponse
    >(`/api/v1/admin/payments/${paymentId}/verify`, request);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Attaches already-uploaded files to a payment as transfer proof.
   * Files are appended to whatever the payment already carries; a Pending payment moves to
   * AwaitingVerification so it becomes verifiable.
   *
   * @param paymentId - The payment ID
   * @param attachmentIds - Attachment IDs returned by adminUploadService.uploadFile
   * @throws Error if the payment is already settled or the response is an error response
   */
  async attachPaymentProof(
    paymentId: string,
    attachmentIds: string[],
  ): Promise<void> {
    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      `/api/v1/admin/payments/${paymentId}/attachments`,
      { attachmentIds },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Removes one proof file from a payment (soft delete of the attachment reference).
   *
   * @param refId - The attachment reference ID, from PaymentAttachmentDto.refId
   * @throws Error if the reference is not found or the response is an error response
   */
  async removePaymentProof(refId: string): Promise<void> {
    const response = await apiClient.delete<MessageResponse | ErrorResponse>(
      `/api/v1/attachments/refs/${refId}`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Writes or clears the payment's note (SuperAdmin only).
   *
   * Accepted in every status, Completed included — the note records what an operator knows about
   * the payment, not a step in deciding it. Passing an empty string clears the note.
   *
   * It writes the same `verificationNotes` field a decision writes, so the customer reads it and
   * a later verify / reject / cancel / refund overwrites it.
   *
   * @param paymentId - The payment ID
   * @param note - The note text, or null/empty to clear it
   * @throws Error if the payment is not found or the response is an error response
   */
  async updatePaymentNote(
    paymentId: string,
    note: string | null,
  ): Promise<void> {
    const response = await apiClient.put<MessageResponse | ErrorResponse>(
      `/api/v1/admin/payments/${paymentId}/note`,
      { note },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Rejects a payment with reason.
   * Marks payment as rejected and notifies tenant.
   *
   * @param paymentId - The payment ID
   * @param request - Rejection request with reason
   * @returns Payment rejection result
   * @throws Error if payment not found, invalid status, or response is an error response
   */
  async rejectPayment(
    paymentId: string,
    request: PaymentRejectionRequest,
  ): Promise<PaymentRejectedDto> {
    const response = await apiClient.post<
      StandardResponseWithMessage<PaymentRejectedDto> | ErrorResponse
    >(`/api/v1/admin/payments/${paymentId}/reject`, request);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Cancels several unsettled payments in one call, for clearing the verification queue of rows
   * nobody is going to confirm.
   *
   * Partial by design: rows that are no longer Pending or AwaitingVerification are reported as
   * skipped rather than failing the batch, so a filtered page can be selected wholesale. The
   * response is always a per-payment report — read `results`, not just the counts.
   *
   * No money moves, and the order behind each payment is left alone so the customer can start a
   * new payment on it.
   */
  async bulkCancelPayments(
    request: BulkCancelPaymentsRequest,
  ): Promise<BulkCancelPaymentsResult> {
    const response = await apiClient.post<
      StandardResponse<BulkCancelPaymentsResult> | ErrorResponse
    >(`/api/v1/admin/payments/bulk-cancel`, request);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * What a refund of this payment would do. Fetched when the dialog opens, because the two things
   * it reports — money already in the balance, and machines left running — cannot be undone from
   * the console once the refund has gone through.
   */
  async getRefundPreview(paymentId: string): Promise<RefundPreviewDto> {
    const response = await apiClient.get<
      StandardResponse<RefundPreviewDto> | ErrorResponse
    >(`/api/v1/admin/payments/${paymentId}/refund-preview`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },

  /**
   * Refunds a completed ORDER payment (SuperAdmin only).
   * Marks the payment refunded and CREDITS the amount back to the tenant's balance — nothing is
   * paid out to a bank. A balance top-up is refused; correct one of those with Reject before it is
   * credited, or Tenant → Adjust balance afterwards.
   *
   * @param paymentId - The payment ID
   * @param request - Refund request; the reason is required and must be at least 10 characters
   * @returns Payment refund result with new balance and transaction ID
   * @throws Error if payment not found, invalid status (not completed), or response is an error response
   */
  async refundPayment(
    paymentId: string,
    request: PaymentRefundRequest,
  ): Promise<PaymentRefundedDto> {
    const response = await apiClient.post<
      StandardResponseWithMessage<PaymentRefundedDto> | ErrorResponse
    >(`/api/v1/admin/payments/${paymentId}/refund`, request);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return response.data.data;
  },
};
